import React, { useState, useEffect, useMemo } from 'react';
import { C, T } from 'shared/helpers';
import propTypes from 'prop-types';

import Styles from './Table.module.scss';
import { useIntl } from 'react-intl';

const ALLOWED_CHARS = /[^0-9,.]+/;

export default function Table({ data, productionUnit, impactos, chartRef }) {
  const { formatNumber } = useIntl();
  const { Table, Row, Block, First, Last, Green, Darker } = Styles;
  const [[dataset], steChart] = useState([]);

  const [chartData, setChartData] = useState({
    header: ['#', 'Recomendação SQM VITAS Brasil', 'Recomendação Convencional'],
    rows: [],
  });

  let divCount = 0;
  const k = () => `Table${++divCount}`;

  const { anychart } = window;

  useEffect(() => {
    // create column chart
    const chart = anychart.column();

    chart.yAxis().labels().format('{%Value}{groupsSeparator:}%');

    // set titles for Y-axis
    chart.yAxis().title('Classificação de Produtos por Qualidade');

    chart
      .labels()
      .enabled(true)
      .position('center-top')
      .anchor('center-bottom')
      .format('{%Value}{groupsSeparator:}%');
    chart.hovered().labels(false);

    // turn on legend and tune it
    chart.legend().enabled(true).fontSize(13).padding([0, 0, 20, 0]);

    // interactivity settings and tooltip position
    chart.interactivity().hoverMode('single');

    chart
      .tooltip()
      .positionMode('point')
      .position('center-top')
      .anchor('center-bottom')
      .offsetX(0)
      .offsetY(5)
      .titleFormat('{%X}')
      .format('{%SeriesName} : {%Value}{groupsSeparator:}%');

    // turn on chart animation
    chart.animation(true);

    // set container id for the chart
    chart.container('chartContainer');

    const dataSet = anychart.data.set([]);

    const seriesData_1 = dataSet.mapAs({
      x: 0,
      value: 2,
    });

    const seriesData_2 = dataSet.mapAs({
      x: 0,
      value: 1,
    });

    // create first series with mapped data
    let series = chart.column(seriesData_1);
    series.name(chartData.header[2]);
    series.normal().fill('#f26c31');
    series.normal().stroke('#f26c31');

    // create second series with mapped data
    series = chart.column(seriesData_2);
    series.name(chartData.header[1]);
    series.normal().fill('#00b050');
    series.normal().stroke('#00b050');

    chart.draw();
    chart.padding(0);
    chart.yAxis(0).width(210).ticks(null);

    chartRef.current = chart;
    steChart([dataSet]);
  }, [anychart, chartData.header, chartRef]);

  useEffect(() => {
    const rows = [];
    for (let i = 0; i < T(T(T(impactos).classificacao).length, 0); i++) {
      rows.push([
        impactos.classificacao[i],
        cleanUp(data[i + 2][0]),
        cleanUp(data[i + 2][1]),
      ]);
    }
    setChartData((x) => ({ ...x, rows }));
  }, [data, impactos]);

  useEffect(() => {
    if (!dataset) return;

    const rowsToRender = chartData.rows.filter((r) => r[1] || r[2]);

    rowsToRender.forEach((r, i) => {
      dataset.row(i, r);
    });

    for (let i = rowsToRender.length; i < dataset.getRowsCount(); i++) {
      dataset.remove(i);
    }
  }, [chartData, dataset]);

  const descarte = Object.values(T(T(impactos).classificacao, {})).slice(-1)[0];

  const Perguntas = [
    `Produtividade em ${productionUnit}/ha`,
    'Custo fertilização',
    ...Object.values(T(T(impactos).classificacao, {})).map(
      (x) => `% produto ${x !== descarte ? `classificado ${x}` : x}`,
    ),
    ...Object.values(T(T(impactos).classificacao, {}))
      .filter((c) => c !== descarte)
      .map((x) => `Preço do produto ${x}`),
    'Preço médio do produto obtido por qualidade',
    'Receita Bruta',
    '"Receita Líquida" (Receita bruta menos somente o custo nutricional)',
  ];

  const Rf = (questionIndex, columnIndex, ...contents) =>
    contents.map((content) => (
      <div
        key={k()}
        id={`pergunta_${questionIndex}_coluna_${columnIndex}`}
        className={C(Block, ...content.slice(1))}
      >
        {content.slice(0, 1)}
      </div>
    ));

  const cleanUp = (s) => {
    const cleaned = s.replace(ALLOWED_CHARS, '').replace(',', '.');
    return Number(cleaned);
  };

  const [precoMedioSQM, precoMedioConv, , precoMedioDiff] = useMemo(
    () => data.slice(-3, -2)[0],
    [data],
  );

  return (
    <div className={Table}>
      <header>
        <div style={{ minWidth: '100%' }}>
          IMPACTO DE QUALIDADE: por diferenças na classificação de qualidade e
          preços de produção obtida
        </div>
      </header>
      <header>
        <div style={{ width: '28%' }}>Item</div>
        <div>Recomendação SQM VITAS BRASIL</div>
        <div>Recomendação Convencional</div>
        <div>Diferença</div>
        <div>Diferença %</div>
        <div>Diferenças em valores (Área total)</div>
      </header>
      <main>
        {Perguntas.map((pergunta, index) => (
          <>
            <div
              key={k()}
              className={C(
                Row,
                [index > Perguntas.length - 4, Last],
                [!data[index].slice(-1)[0], Styles.Hidden],
              )}
            >
              <div className={C(Block, First)}>{pergunta}</div>
              {data[index]
                .slice(0, -1)
                .map((blob, blobi) =>
                  Rf(index, blobi, [
                    blob,
                    [0, 1].includes(blobi) && index > Perguntas.length - 3
                      ? Darker
                      : Green,
                  ]),
                )}
            </div>
          </>
        ))}
        <div key={k()} className={C(Row, Last)}>
          <div style={{ minWidth: '100%' }} className={C(Block, First)} />
        </div>
      </main>
      <br />
      <div className={Styles.ChartContainer}>
        <div id='chartContainer' style={{ width: '100%', height: '500px' }} />
        <div className={[Styles.Overlay, Styles.OverlayBase].join(' ')}>
          <div className={Styles.Block}>
            <div>Preço médio obtido por qualidade</div>
          </div>
          <div>
            <div>CONVENCIONAL</div>
            <div>SQM VITAS BRASIL</div>
            <div>DIFERENÇA</div>
          </div>
          <div>
            <div>{precoMedioConv}</div>
            <div>{precoMedioSQM}</div>
            <div>{precoMedioDiff}</div>
          </div>
        </div>
        <div className={Styles.LabelTable}>
          <div>
            {chartData.rows
              .filter((r) => r[1] || r[2])
              .reduce(
                (r, [, sqm, conv]) => [
                  [...r[0], conv],
                  [...r[1], sqm],
                  [...r[2], ((sqm - conv) / conv) * 100],
                ],
                [
                  ...[
                    ...chartData.header.slice(1).reverse(),
                    'Diferença %',
                  ].map((i) => [i]),
                ],
              )
              .map((rowData, i1) => (
                <div key={i1.toString()}>
                  {rowData.map((data, i2) => (
                    <div key={`${i1.toString()}${i2.toString()}`}>
                      {i2 === 0
                        ? data
                        : `${data < 0 ? '' : '+'}${formatNumber(
                            data === Infinity ? 0 : data || 0,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}${' '}%`}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

Table.defaultProps = {
  productionUnit: 'kg',
};

Table.propTypes = {
  data: propTypes.any.isRequired,
  productionUnit: propTypes.string,
  impactos: propTypes.any.isRequired,
};
